/**
 * BackupActivityToast — Glassmorphic toast notifications for S3 & backup activities.
 * Mounted at AdminDashboard level so it appears on every admin page.
 * Toasts stack compactly by default and expand on hover.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

// ── Types ──
interface ScheduleInfo {
  lastRunAt: number | null;
  intervalMs: number;
  nextRunAt: number | null;
  running: boolean;
}

interface ToastItem {
  id: string;
  type: 'session' | 'uploads' | 'database';
  title: string;
  subtitle: string;
  seconds: number;
  isExiting: boolean;
}

// ── Animations ──
const slideIn = keyframes`
  0% { transform: translateX(100%) scale(0.92); opacity: 0; }
  100% { transform: translateX(0) scale(1); opacity: 1; }
`;

const slideOut = keyframes`
  0% { transform: translateX(0) scale(1); opacity: 1; }
  100% { transform: translateX(110%) scale(0.92); opacity: 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const progressSlide = keyframes`
  0% { background-position: 0% 0; }
  100% { background-position: 200% 0; }
`;

// ── Color themes (light / pastel) ──
const themes = {
  session: {
    bg: 'rgba(250, 245, 255, 0.72)',
    border: 'rgba(168, 85, 247, 0.25)',
    accent: '#8b5cf6',
    accentSoft: '#a78bfa',
    textTitle: '#581c87',
    textSub: '#7e22ce',
    iconBg: 'rgba(139, 92, 246, 0.12)',
    progressBg: 'rgba(139, 92, 246, 0.12)',
    progressFill: 'linear-gradient(90deg, #a78bfa, #8b5cf6, #a78bfa)',
    dotColor: '#8b5cf6',
    badgeBg: 'rgba(139, 92, 246, 0.08)',
    shadow: 'rgba(139, 92, 246, 0.12)',
  },
  uploads: {
    bg: 'rgba(239, 246, 255, 0.72)',
    border: 'rgba(59, 130, 246, 0.25)',
    accent: '#3b82f6',
    accentSoft: '#60a5fa',
    textTitle: '#1e3a5f',
    textSub: '#2563eb',
    iconBg: 'rgba(59, 130, 246, 0.12)',
    progressBg: 'rgba(59, 130, 246, 0.12)',
    progressFill: 'linear-gradient(90deg, #60a5fa, #3b82f6, #60a5fa)',
    dotColor: '#3b82f6',
    badgeBg: 'rgba(59, 130, 246, 0.08)',
    shadow: 'rgba(59, 130, 246, 0.12)',
  },
  database: {
    bg: 'rgba(240, 253, 244, 0.72)',
    border: 'rgba(34, 197, 94, 0.25)',
    accent: '#16a34a',
    accentSoft: '#4ade80',
    textTitle: '#14532d',
    textSub: '#15803d',
    iconBg: 'rgba(34, 197, 94, 0.12)',
    progressBg: 'rgba(34, 197, 94, 0.12)',
    progressFill: 'linear-gradient(90deg, #4ade80, #22c55e, #4ade80)',
    dotColor: '#22c55e',
    badgeBg: 'rgba(34, 197, 94, 0.08)',
    shadow: 'rgba(34, 197, 94, 0.12)',
  },
};

// ── SVG Icons ──
const SvgSession = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const SvgUploads = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const SvgDatabase = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const iconComponents: Record<string, React.FC> = {
  session: SvgSession,
  uploads: SvgUploads,
  database: SvgDatabase,
};

// ── Styled Components ──
const COLLAPSED_HEIGHT = 6; // px gap between stacked toasts when collapsed

const Container = styled.div<{ $hovered: boolean }>`
  position: fixed;
  top: 14px;
  right: 16px;
  z-index: 10002;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  gap: ${p => p.$hovered ? '8px' : '0px'};
  transition: gap 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 480px) {
    right: 8px;
    left: 8px;
  }
`;

const Toast = styled.div<{
  $type: 'session' | 'uploads' | 'database';
  $isExiting: boolean;
  $index: number;
  $total: number;
  $hovered: boolean;
}>`
  background: ${p => themes[p.$type].bg};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid ${p => themes[p.$type].border};
  min-width: 260px;
  max-width: 300px;
  box-shadow:
    0 4px 20px ${p => themes[p.$type].shadow},
    0 1px 4px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  animation: ${p => p.$isExiting ? slideOut : slideIn}
    ${p => p.$isExiting ? '0.3s' : '0.4s'}
    cubic-bezier(0.16, 1, 0.3, 1) forwards;
  pointer-events: auto;
  position: relative;
  overflow: hidden;
  cursor: default;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.35s ease,
              margin-top 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  /* Stacked effect when not hovered: each subsequent toast shifts down & slightly smaller */
  ${p => !p.$hovered && p.$index > 0 && !p.$isExiting ? css`
    margin-top: -${Math.max(44 - COLLAPSED_HEIGHT, 0)}px;
    transform: scale(${1 - p.$index * 0.03}) translateY(${p.$index * 2}px);
    opacity: ${Math.max(1 - p.$index * 0.15, 0.4)};
  ` : ''}

  ${p => p.$hovered ? css`
    transform: scale(1) translateY(0);
    opacity: 1;
    margin-top: 0;
  ` : ''}

  @media (max-width: 480px) {
    min-width: unset;
    max-width: unset;
    width: 100%;
  }
`;

const ToastBody = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
`;

const IconBox = styled.div<{ $type: 'session' | 'uploads' | 'database' }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${p => themes[p.$type].iconBg};
  color: ${p => themes[p.$type].accent};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div<{ $type: 'session' | 'uploads' | 'database' }>`
  font-size: 11.5px;
  font-weight: 650;
  color: ${p => themes[p.$type].textTitle};
  letter-spacing: 0.1px;
  line-height: 1.3;
`;

const Subtitle = styled.div<{ $type: 'session' | 'uploads' | 'database' }>`
  font-size: 10px;
  font-weight: 500;
  color: ${p => themes[p.$type].textSub};
  opacity: 0.75;
  line-height: 1.3;
  margin-top: 1px;
`;

const StatusRow = styled.div<{ $type: 'session' | 'uploads' | 'database' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => themes[p.$type].accent};
  background: ${p => themes[p.$type].badgeBg};
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 3px;
`;

const PulseDot = styled.span<{ $type: 'session' | 'uploads' | 'database' }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${p => themes[p.$type].dotColor};
  animation: ${pulseGlow} 1.5s ease-in-out infinite;
`;

const CountdownBadge = styled.div<{ $type: 'session' | 'uploads' | 'database' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => themes[p.$type].iconBg};
  border: 1.5px solid ${p => themes[p.$type].border};
  flex-shrink: 0;
`;

const CountdownNum = styled.span<{ $type: 'session' | 'uploads' | 'database' }>`
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${p => themes[p.$type].accent};
  line-height: 1;
`;

const CountdownLabel = styled.span<{ $type: 'session' | 'uploads' | 'database' }>`
  font-size: 7px;
  font-weight: 600;
  color: ${p => themes[p.$type].textSub};
  opacity: 0.6;
  letter-spacing: 0.3px;
  line-height: 1;
`;

const ProgressBar = styled.div<{ $type: 'session' | 'uploads' | 'database' }>`
  height: 2px;
  background: ${p => themes[p.$type].progressBg};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $type: 'session' | 'uploads' | 'database'; $pct: number }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: ${p => themes[p.$type].progressFill};
  background-size: 200% 100%;
  animation: ${progressSlide} 2s linear infinite;
  transition: width 1s linear;
`;

const CloseBtn = styled.button<{ $type: 'session' | 'uploads' | 'database' }>`
  position: absolute;
  top: 4px;
  right: 4px;
  background: transparent;
  border: none;
  color: ${p => themes[p.$type].accent};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;
  padding: 0;
  font-size: 12px;
  line-height: 1;

  ${Toast}:hover & {
    opacity: 0.5;
  }

  &:hover {
    opacity: 1 !important;
    background: ${p => themes[p.$type].iconBg};
  }
`;

// ── Constants ──
const WARN_THRESHOLD = 30;
const TOAST_DURATION = 35000;

const titles: Record<string, string> = {
  session: 'Session Recording Migration',
  uploads: 'Uploads S3 Sync',
  database: 'Database Backup',
};

// ── Component ──
const BackupActivityToast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [hovered, setHovered] = useState(false);
  const [sessionSchedule, setSessionSchedule] = useState<ScheduleInfo | null>(null);
  const [backupSchedule, setBackupSchedule] = useState<ScheduleInfo | null>(null);
  const firedRef = useRef<Set<string>>(new Set());

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/s3/schedules`, { withCredentials: true });
      if (res.data.success) {
        setSessionSchedule(res.data.sessionMigration);
        setBackupSchedule(res.data.s3Backup);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchSchedules();
    const interval = setInterval(fetchSchedules, 60000);
    return () => clearInterval(interval);
  }, [fetchSchedules]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, isExiting: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 350);
  }, []);

  // Fire toasts on countdown
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const checks: { type: 'session' | 'uploads' | 'database'; schedule: ScheduleInfo | null }[] = [
        { type: 'session', schedule: sessionSchedule },
        { type: 'uploads', schedule: backupSchedule },
        { type: 'database', schedule: backupSchedule },
      ];

      for (const { type, schedule } of checks) {
        if (!schedule || !schedule.nextRunAt || schedule.running) continue;
        const remaining = Math.max(0, Math.floor((schedule.nextRunAt - now) / 1000));
        const cycleKey = `${type}-${schedule.nextRunAt}`;

        if (remaining <= WARN_THRESHOLD && remaining > 0 && !firedRef.current.has(cycleKey)) {
          firedRef.current.add(cycleKey);
          const id = `${type}-${now}`;
          const subtitle = type === 'session'
            ? 'Migrating recordings to S3'
            : type === 'uploads'
              ? 'Syncing uploads to S3 bucket'
              : 'Backing up database to S3';

          setToasts(prev => [...prev, { id, type, title: titles[type], subtitle, seconds: remaining, isExiting: false }]);
          setTimeout(() => removeToast(id), TOAST_DURATION);
        }

        if (remaining === 0 && !firedRef.current.has(`${cycleKey}-started`)) {
          firedRef.current.add(`${cycleKey}-started`);
          const startId = `${type}-started-${now}`;
          setToasts(prev => [...prev, {
            id: startId, type, title: `${titles[type]}`,
            subtitle: 'Running now...', seconds: 0, isExiting: false,
          }]);
          setTimeout(() => removeToast(startId), 8000);
        }
      }

      if (firedRef.current.size > 50) {
        firedRef.current = new Set(Array.from(firedRef.current).slice(-20));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionSchedule, backupSchedule, removeToast]);

  // Live countdown tick
  useEffect(() => {
    const interval = setInterval(() => {
      setToasts(prev => prev.map(t => {
        if (t.isExiting || t.seconds <= 0) return t;
        return { ...t, seconds: Math.max(0, t.seconds - 1) };
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <Container
      $hovered={hovered}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {toasts.map((toast, idx) => {
        const pct = toast.seconds > 0 ? ((WARN_THRESHOLD - toast.seconds) / WARN_THRESHOLD) * 100 : 100;
        const Icon = iconComponents[toast.type];
        return (
          <Toast
            key={toast.id}
            $type={toast.type}
            $isExiting={toast.isExiting}
            $index={idx}
            $total={toasts.length}
            $hovered={hovered}
          >
            <ToastBody>
              <IconBox $type={toast.type}><Icon /></IconBox>
              <Content>
                <Title $type={toast.type}>{toast.title}</Title>
                <Subtitle $type={toast.type}>{toast.subtitle}</Subtitle>
                {toast.seconds > 0 ? (
                  <StatusRow $type={toast.type}>
                    <PulseDot $type={toast.type} />
                    in {toast.seconds}s
                  </StatusRow>
                ) : (
                  <StatusRow $type={toast.type}>
                    <PulseDot $type={toast.type} />
                    running
                  </StatusRow>
                )}
              </Content>
              {toast.seconds > 0 && (
                <CountdownBadge $type={toast.type}>
                  <CountdownNum $type={toast.type}>{toast.seconds}</CountdownNum>
                  <CountdownLabel $type={toast.type}>sec</CountdownLabel>
                </CountdownBadge>
              )}
            </ToastBody>
            <ProgressBar $type={toast.type}>
              <ProgressFill $type={toast.type} $pct={pct} />
            </ProgressBar>
            <CloseBtn $type={toast.type} onClick={() => removeToast(toast.id)}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="2" y1="2" x2="8" y2="8" /><line x1="8" y1="2" x2="2" y2="8" />
              </svg>
            </CloseBtn>
          </Toast>
        );
      })}
    </Container>
  );
};

export default BackupActivityToast;
